import {
    type TextStyle,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    CodeEditorColorsConstantType,
    CodeEditorDynamicStyleType,
    CodeEditorColors
} from "./type";
import type {
    Mutable
} from "../../types";
import type ISystemToolbarProps from "../systemToolbar/type";
import {
    getCodeGutterMeasures,
    getCodeTextStyle
} from "../codeViewer/stylesheet";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getTouchSafeTextStyle
} from "../../helpers/theme/input";
import {
    getFieldFocusStyle
} from "../../helpers/theme/state";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import {
    webStyle
} from "../../utils";

export const CODE_EDITOR_TOOLBAR_ACTION: Pick<ISystemToolbarProps, "variant" | "size" | "type"> = {
    variant: "ghost",
    type: "neutral",
    size: "small"
};

export const getCodeEditorColors = ({
    isDisabled,
    isReadOnly,
    colors
}: CodeEditorColorsConstantType): CodeEditorColors => {
    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            containerColor: colors.system.state.overlay.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral,
            activeLineNumberColor: disabledColor,
            lineNumberColor: disabledColor,
            textColor: disabledColor
        };
    }

    return {
        containerColor: isReadOnly ? colors.content.container.mid : colors.content.container.raised,
        borderColor: isReadOnly ? colors.content.border.subtle : colors.content.border.default,
        activeLineNumberColor: colors.content.text.high,
        lineNumberColor: colors.content.text.low,
        textColor: colors.code.text
    };
};

const stylesheet = StyleSheet.create({
    input: {
        backgroundColor: "transparent",
        textAlignVertical: "top",
        position: "absolute",
        color: "transparent",
        paddingVertical: 0,
        borderWidth: 0,
        bottom: 0,
        margin: 0,
        start: 0,
        top: 0,
        end: 0,
        ...webStyle({
            fontVariantLigatures: "none",
            overflowWrap: "normal",
            overflow: "hidden",
            whiteSpace: "pre",
            resize: "none"
        })
    },
    codeText: {
        ...webStyle({
            fontVariantLigatures: "none",
            whiteSpace: "pre"
        })
    },
    line: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    disabledContainer: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    content: {
        flexDirection: "column",
        minWidth: "100%",
        flexGrow: 1
    },
    container: {
        flexDirection: "column",
        borderStyle: "solid"
    },
    scroll: {
        ...Platform.select({
            default: {
                direction: "ltr"
            },
            web: {
            }
        })
    },
    lineNumber: {
        textAlign: "right",
        flexShrink: 0,
        ...webStyle({
            userSelect: "none"
        })
    },
    gutterDivider: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        top: 0
    },
    stretchedContainer: {
        alignSelf: "stretch",
        flexShrink: 1,
        width: "100%"
    },
    toolbar: {
        flexDirection: "row",
        alignItems: "center",
        borderStyle: "solid",
        flexWrap: "wrap"
    },
    statusBar: {
        flexDirection: "row",
        borderStyle: "solid",
        flexWrap: "wrap"
    },
    code: {
        flexDirection: "row",
        alignItems: "center",
        flexGrow: 1
    },
    editor: {
        position: "relative",
        overflow: "hidden"
    },
    lines: {
        position: "relative"
    },
    scrollableEditor: {
        flexShrink: 1,
        flexGrow: 1
    },
    toolbarSpacer: {
        flexGrow: 1
    }
});

export const useStyles = ({
    isLineNumberVisible,
    isToolbarVisible,
    isShowStatusBar,
    spreadBehaviour,
    borderRadius,
    editorColors,
    isScrollable,
    shapeConfigs,
    pointerType,
    shadowAlpha,
    elevations,
    isDisabled,
    isReadOnly,
    typography,
    hitMargin,
    iconSizes,
    isFocused,
    userStyle,
    borders,
    colors,
    spaces
}: CodeEditorDynamicStyleType) => {
    const codeTextStyle = getTouchSafeTextStyle({
        textStyle: getCodeTextStyle({
            typography
        }),
        pointerType,
        typography
    });

    const gutterMeasures = getCodeGutterMeasures({
        isCollapsible: false,
        isLineNumberVisible,
        iconSizes,
        spaces
    });

    const innerRadius = concentric({
        inset: borders.line,
        outer: borderRadius,
        role: borderRadius
    });

    const {
        borderRadius: scaledInnerRadius,
        ...innerShape
    } = getCornerStyle({
        radius: innerRadius,
        shapeConfigs
    });

    const styles = {
        activeLine: {
            backgroundColor: colors.system.state.overlay.hover.default
        } as Mutable<ViewStyle>,
        input: {
            ...codeTextStyle,
            paddingStart: gutterMeasures.codePaddingStart,
            paddingEnd: gutterMeasures.codePaddingEnd,
            ...webStyle({
                caretColor: colors.system.state.overlay.focus.default,
                outlineStyle: "solid",
                outlineWidth: 0
            })
        } as Mutable<TextStyle>,
        toolbar: {
            borderBottomColor: colors.content.border.subtle,
            borderBottomWidth: borders.separator,
            padding: spaces.spacingXs,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        code: {
            paddingStart: gutterMeasures.codePaddingStart,
            paddingEnd: gutterMeasures.codePaddingEnd
        } as Mutable<ViewStyle>,
        gutterDivider: {
            backgroundColor: colors.content.border.subtle,
            start: gutterMeasures.numberWidth,
            width: borders.line
        } as Mutable<ViewStyle>,
        statusBar: {
            borderTopColor: colors.content.border.subtle,
            paddingHorizontal: spaces.spacingMd,
            borderTopWidth: borders.separator,
            paddingVertical: spaces.spacingXs,
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        container: {
            backgroundColor: editorColors.containerColor,
            borderColor: editorColors.borderColor,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        activeLineNumber: {
            color: editorColors.activeLineNumberColor
        } as Mutable<TextStyle>,
        lineNumber: {
            ...codeTextStyle,
            color: editorColors.lineNumberColor,
            width: gutterMeasures.numberWidth,
            paddingEnd: spaces.spacingXs
        } as Mutable<TextStyle>,
        line: {
            minHeight: codeTextStyle.lineHeight
        } as Mutable<ViewStyle>,
        language: {
            paddingHorizontal: spaces.spacingSm
        } as Mutable<TextStyle>,
        content: {
            paddingVertical: spaces.spacingSm
        } as Mutable<ViewStyle>,
        codeText: {
            ...codeTextStyle,
            color: editorColors.textColor
        } as Mutable<TextStyle>,
        toolbarAction: {
        } as Mutable<ViewStyle>,
        editor: {
            ...innerShape
        } as Mutable<ViewStyle>
    };

    if(hitMargin) {
        styles.toolbarAction.marginVertical = hitMargin;
    }

    if(isLineNumberVisible) {
        styles.input.start = gutterMeasures.numberWidth;
    }

    if(!isToolbarVisible) {
        styles.editor.borderTopStartRadius = scaledInnerRadius;
        styles.editor.borderTopEndRadius = scaledInnerRadius;
    }

    if(!isShowStatusBar) {
        styles.editor.borderBottomStartRadius = scaledInnerRadius;
        styles.editor.borderBottomEndRadius = scaledInnerRadius;
    }

    if(!isDisabled && !isReadOnly) {
        Object.assign(styles.container, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    if(!isDisabled) {
        Object.assign(styles.container, getFieldFocusStyle({
            isFocused,
            borders,
            colors
        }));
    }

    if(isDisabled) {
        Object.assign(styles.container, stylesheet.disabledContainer);
    }

    if(isScrollable) {
        Object.assign(styles.editor, stylesheet.scrollableEditor);
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, getHugStyle({
            style: userStyle
        }));
    }

    if(spreadBehaviour === "stretch") {
        Object.assign(styles.container, stylesheet.stretchedContainer);
    }

    return styles;
};
export default stylesheet;
